import { get, startCase } from "lodash";

import { MultiForm } from "app/adminApp/components";
import mixpanel from "mixpanel-browser";
import useActiveBlockSheet from "app/utils/useActiveBlockSheet";
import { useState } from "react";

const getLabel = (filter) => {
  if (filter.key) {
    const key = startCase(filter.key);
    const operator = get(filter, "operator", "equals");

    if (operator.includes("date")) {
      return startCase(`${key} ${startCase(operator.replace("date_", ""))}`);
    }

    return `${key} ${startCase(operator)}`;
  }

  return "New Visible Filter";
};

const getVisibleFilterFields = (activeFilter, operatorSelectContextObj) => {
  return [
    {
      key: "label",
      label: "Label",
      hint: "A custom visual label",
      componentId: "Input",
    },
    {
      key: "key",
      label: "Column",
      hint: "Which column to use for matching the row value",
      componentId: "SpreadsheetColumnSelect",
    },
    {
      key: "operator",
      label: "Operator",
      hint: "The operator to use for matching the row value",
      componentId: "OperatorSelect",
      hideEmptyItem: true,
      defaultValue: "equals",
      ...operatorSelectContextObj,
      displayCondition: (f) =>
        get(f, "key") &&
        get(f, "componentId") &&
        !["DateRangePicker", "Boolean", "NumericRange"].includes(
          get(f, "componentId")
        ),
    },
    {
      key: "defaultValue",
      label: "Default Value",
      componentId: "DynamicString",
      displayCondition: (f) =>
        !["DateRangePicker", "NumericRange"].includes(get(f, "componentId")),
    },
    {
      key: "selectSorting",
      label: "Sort Options",
      componentId: "Select",
      requiresSheet: true,
      hideEmptyItem: true,
      options: [
        { label: "None", value: null },
        { label: "Ascending", value: "asc" },
        { label: "Descending", value: "desc" },
      ],
      advanced: true,
      displayCondition: (f) =>
        ["Select", "MultiSelect"].includes(get(f, "componentId")),
    },
    {
      key: "componentId",
      label: "Component",
      hint: "The type of input field for users to filter their data with",
      componentId: "Select",
      defaultValue: "Input",
      hideEmptyItem: true,
      options: [
        { label: "Input", value: "Input" },
        { label: "Numeric Range", value: "NumericRange" },
        { label: "True/False", value: "Boolean" },
        { label: "Select Dropdown", value: "Select" },
        { label: "Multi Select", value: "MultiSelect" },
        { label: "Date", value: "DateTimePicker" },
        { label: "Date Range Picker", value: "DateRangePicker" },
      ],
      displayCondition: (f) => get(f, "key"),
    },
    {
      key: "preventAutoGeneratedOptions",
      label: "Prevent Auto Generated Options",
      hint: "By default, Frontly will show all valid options from the live data as select options. Check this box to prevent that.",
      componentId: "Switch",
      requiresSheet: true,
      advanced: true,
      displayCondition: (f) =>
        ["Select", "MultiSelect"].includes(get(f, "componentId")),
    },
    {
      key: "useDataSourceOptions",
      label: "Use Data Source Options",
      hint: "Enable this setting to use the options from the data source instead of defining the options here.",
      componentId: "Switch",
      requiresSheet: true,
      advanced: true,
      displayCondition: (f) =>
        ["Select", "MultiSelect"].includes(get(f, "componentId")),
    },
    {
      key: "options",
      label: "Options",
      componentId: "DataGrid",
      orientation: "vertical",
      hint: "Define the options to appear in your select dropdown.",
      requiresSheet: true,
      newObject: { label: "Option 1", value: "option_1" },
      orientation: "vertical",
      columns: [
        {
          key: "label",
          componentId: "Input",
        },
        {
          key: "value",
          componentId: "Input",
        },
      ],
      displayCondition: (f) =>
        !get(f, "useDataSourceOptions") &&
        ["Select", "MultiSelect"].includes(get(f, "componentId")),
    },
    {
      key: "icon",
      label: "Icon",
      componentId: "IconBrowser",
      displayCondition: (f) => get(f, "key") && get(f, "componentId"),
    },
  ]
    .filter(
      (f) =>
        !f.displayCondition ||
        (f.displayCondition && f.displayCondition(activeFilter))
    )
    .map((f) => ({ ...f, orientation: "horizontal", width: "200px" }));
};

const VisibleFiltersConfig = ({ data }) => {
  const activeBlockSheet = useActiveBlockSheet();

  const filters = get(data, "value", []);

  const [activeFilterIndex, setActiveFilterIndex] = useState(null);

  const activeFilter = filters.find((f, i) => i === activeFilterIndex);

  const operatorSelectContextObj = {
    context: "visibleFilters",
    service: get(activeBlockSheet, "service"),
    fieldConfig: get(activeBlockSheet, [
      "field_data",
      "config",
      get(activeFilter, "key", null),
    ]),
  };

  const fields = getVisibleFilterFields(activeFilter, operatorSelectContextObj);

  return (
    <MultiForm
      data={{
        label: "Filters",
        // width: "370px",
        orientation: "vertical",
        labelSingular: "Visible Filter",
        componentId: "MultiForm",
        value: filters,
        fields,
        newObject: { componentId: "Input" },
        getItemLabel: (f) => getLabel(f),
        itemClick: (i) => setActiveFilterIndex(i),
        onChange: (newFilters) => {
          data.onChange(newFilters);
          mixpanel.track("Add Visible Filter");
        },
      }}
    />
  );
};

export default VisibleFiltersConfig;
